import Api from "../../services/Api.ts";
import {useEffect, useState} from "react";
import {useAtom, useSetAtom} from 'jotai';
import { categoriesAtom, selectedDepartmentsAtom, matchingClientsAtom } from '../../jotai_state';
import TooFewClients from "../../components/TooFewClients/TooFewClients.tsx";
import {ClientsInterface} from "./interfaces.ts";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend } from 'chart.js';
import {Bar, Scatter} from "react-chartjs-2";
import {rgbColors} from "../../helpers/colors.ts";

ChartJS.register(CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend);

interface AwarenessInterface {
  nr: number;
  clientsWithAwareness: number;
  important: number
  energy: number;
  well: number;
  score: number;
}

interface QuestionCountInterface {
  name: string;
  count: number;
}

interface QuestionInterface {
  name: string;
  scoreSelf: number;
  scoreOther: number;
  nrOther: number;
}

interface ActivatedQuestionsInterface {
  questionCounts: Array<QuestionCountInterface>;
  questions: Array<QuestionInterface>;
}

interface SurveyInterface {
  id: string;
  name: string;
  answerOptions: number;
  activatedQuestions: ActivatedQuestionsInterface;
}

export default function StrategyAlignment() {
  const api = Api.getInstance();
  const [tooFewClients, setTooFewClients] = useState<number|undefined>(undefined);
  const [awareness, setAwareness] = useState<AwarenessInterface|null>(null);
  const [surveys, setSurveys] = useState<Array<SurveyInterface>>([]);
  const [categories] = useAtom(categoriesAtom);
  const [selectedDepartments] = useAtom(selectedDepartmentsAtom);
  const setMatchingClients = useSetAtom(matchingClientsAtom);
  const [clients, setClients] = useState<ClientsInterface>();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getData()
  }, [categories, selectedDepartments]);

  const getData = () => {
    const data = {
      categories: categories,
      departments: selectedDepartments,
    }

    api.post('customer/sprint_strategy_alignment', data).then((response) => {
      if (response.json.data.tooFewClients !== undefined) {
        setTooFewClients(response.json.data.tooFewClients)
      } else {
        setClients(response.json.data.clients);
        setAwareness(response.json.data.awareness);
        setSurveys((response.json.data.surveys));
        setMatchingClients(response.json.data.matchingClients);
        setTooFewClients(undefined);
      }
      setLoaded(true);
    }).catch(error => {
      setError('Unknown error (http status ' + error.response.status + ')')
      setLoaded(true)
    })
  }

  if (!loaded) {
    return <div className="px-8 py-4">Loading data...</div>
  }
  if (error.length > 0) {
    return <div className="px-8 py-4">{error}</div>
  }
  if (!awareness || awareness.nr === 0) {
    return <div className="px-8 py-4">No drivers found</div>
  }
  if (tooFewClients !== undefined) {
    return <TooFewClients tooFewClients={tooFewClients}/>
  }

  const renderAwareness = () => {

    let goalsAverage = 0;
    let percentAwareness = 0;
    if (clients?.activated) {
      goalsAverage = awareness.nr / clients.activated;
      percentAwareness = 100 * awareness.clientsWithAwareness / clients.activated;
    }

    const awarenessData = [awareness.important, awareness.well, awareness.energy]
    const barColors = [];
    for (const d of awarenessData) {
      let color = 0;
      if (d > 2) {
        color = 0;
      } else if (d > 1.5) {
        color = 2;
      } else {
        color = 3;
      }
      barColors.push(rgbColors[color]);
    }


    return (
      <div>
        <div className="mt-4 z-h3">Goal engagement</div>
        <div className="z-body1 mt-2 text-gray-1000!">
          People have engaged the current goals on average {goalsAverage.toFixed(1)} times,
          and {percentAwareness.toFixed(0)}% have activated and have goals they hope to work on.
        </div>

        <div className="z-box mt-4">
          <div className="flex items-baseline">
            <div className="z-title2">TOTAL SCORE</div>
            <div className="z-h3 ml-2">{awareness.score.toFixed(1)}</div>
            <div className="z-title2"> / 10</div>
          </div>
          <div className="my-2 flex items-center z-body2">
            <div className="bg-cherry-600 h-3 w-3 rounded-full"/>
            <div className="ml-1">Critical &lt; 1.5</div>
            <div className="ml-4 bg-orange-500 h-3 w-3 rounded-full"/>
            <div className="ml-1">Risk 1.5 - 2</div>
            <div className="ml-4 bg-aquamarine-600 h-3 w-3 rounded-full"/>
            <div className="ml-1">Strength &gt;= 2</div>
          </div>

          <Bar
            data={{
              labels: ["Importance", "Situation", "Energy"],
              datasets: [{
                label: 'Activated zones',
                data: awarenessData,
                backgroundColor: barColors,
              }],
            }}
            options={{
              responsive: true,
              scales: { y: { min: 0, max: 3 } },
              plugins: { legend: { display: false }, title: { display: false } },
            }}
          />
        </div>

      </div>
    )
  }

  const renderActivatedCount = (survey: SurveyInterface) => {


    const questionScatter: Array<any> = [];
    for (const question of survey.activatedQuestions.questions) {
      questionScatter.push({
        x: question.scoreOther,
        y: question.scoreSelf
      })
    }

    let limit = 4;
    let riskLimits = [3, 4];
    if (survey.answerOptions === 7) {
      limit = 6;
      riskLimits = [4, 6];
    }
    
    const pointBackgroundColors = [];
    const questionType = [
      [
        {name: "Development focus", color: "#f97316", colorCls: "orange-500", count: 0},
        {name: "Hidden strength", color: "#8b5cf6", colorCls: "violet-500", "count": 0},
      ],
      [
        {name: "Blind spot", color: "#b45309", colorCls: "amber-700", "count": 0},
        {name: "Verified strength", color: "#4CA28E", colorCls: "aquamarine-600", count: 0},
      ],
    ]

    let riskNumbers = [0, 0, 0];
    for (const q of survey.activatedQuestions.questions) {
      let posSelf = q.scoreSelf < limit ? 0 : 1;
      let posOther = q.scoreOther < limit ? 0 : 1;
      const _qType = questionType[posSelf][posOther];
      pointBackgroundColors.push(_qType.color);
      _qType.count += 1;
      let posRisk = 2;
      if (q.scoreOther < riskLimits[0]) {
        posRisk = 0;
      } else if (q.scoreOther < riskLimits[1]) {
        posRisk = 1
      }
      riskNumbers[posRisk]++;
    }
    if (survey.activatedQuestions.questions.length > 0) {
      for (let i = 0; i < 2; i++) {
        riskNumbers[i] = 100 * riskNumbers[i] / survey.activatedQuestions.questions.length;
      }
    }

    const questionTypes = [];
    for (let selfPos = 0; selfPos < 2; selfPos++) {
      for (let otherPos = 0; otherPos < 2; otherPos++) {
        questionTypes.push(questionType[selfPos][otherPos]);
      }
    }

    return (
      <>
        <div className="mt-4 z-h2">{survey.name}</div>
        <div className="mt-2 z-h3">Activated questions</div>
        <div className="mt-2 z-body2">Shows what behaviors have been activated in created goals</div>

        <div className="bg-stone-100 mt-2 p-4 rounded-xl">
          <div className="z-title2">{survey.activatedQuestions.questionCounts.length} behaviors</div>
          {
            survey.activatedQuestions.questionCounts.sort((a, b) => (b.count - a.count))
              .map((questionCount, index) => (
                <div key={"questionCount_" + index} className="flex justify-between mt-2">
                  <div>{questionCount.name}</div>
                  {
                    clients &&
                      <div>{(100 * questionCount.count / clients.activated).toFixed(0)}%</div>
                  }
                </div>
              ))
          }
        </div>

        <div className="mt-4 z-h3">Activated questions by type</div>

        <div className="bg-stone-100 mt-2 p-4 rounded-xl">
          <Bar
            data={{
              labels: ["Critical", "Risk", "Strength"],
              datasets: [{
                label: 'Activated zones',
                data: riskNumbers,
                backgroundColor: ['#CC4B3F', '#E2A83F', '#57733F'],
              }],
            }}
            options={{
              responsive: true,
              scales: { y: { min: 0, max: 100, ticks: { callback: (v) => v + '%' } } },
              plugins: { legend: { display: true }, title: { display: false } },
            }}
          />
        </div>

        <div className="bg-stone-100 mt-2 p-4 rounded-xl">
          <div className="z-body3">
            Every point is a behavior that a user has chosen to work with, placed after the user's own score.
            These are not all behaviors, only the ones activated.
          </div>
          <div className="flex justify-between mt-2">
            {
              questionTypes.map((qType, index) => (
                <div key={"qType_" + index}>
                  <div className="z-body3">Addressing {qType.name}</div>
                  <div className="z-h3" style={{color: qType.color}}>{qType.count}</div>
                </div>
              ))
            }
          </div>
          <Scatter
            data={{
              datasets: [{
                label: "Individual responses",
                pointRadius: 2,
                pointBackgroundColor: pointBackgroundColors,
                data: questionScatter,
              }],
            }}
            options={{
              indexAxis: 'y',
              responsive: true,
              scales: {
                x: { min: 0, max: survey.answerOptions, title: {display: true, text: "Feedback"} },
                y: { min: 0, max: survey.answerOptions, title: {display: true, text: "Self"} }
              },
            }}
          />
        </div>

        {/*<div className="flex justify-between mt-2">*/}
        {/*  <div>Question</div>*/}
        {/*  <div className="flex gap-x-2">*/}
        {/*    <div className="w-12">Self</div>*/}
        {/*    <div className="w-12">Feedback</div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*{*/}
        {/*  survey.activatedQuestions.questions.sort((a, b) => (b.scoreOther - a.scoreOther))*/}
        {/*    .map((question, index) => (*/}
        {/*      <div key={"question_" + index} className="flex justify-between mt-2">*/}
        {/*        <div>{index + 1}. {question.name}</div>*/}
        {/*        <div className="flex gap-x-2">*/}
        {/*          <div className="w-12">{question.scoreSelf.toFixed(1)}</div>*/}
        {/*          <div className="w-12">{question.scoreOther.toFixed(1)}</div>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    ))*/}
        {/*}*/}
      </>
    )

  }

  return (
    <>
      <div className="z-h2">Strategic alignment</div>
      <div className="z-body1 mt-4 text-gray-1000!">
        Whether what employees actually spend time on matches what the organization has said should be prioritized.
      </div>
      <div className="z-body3">Users: {clients?.total} invited, {clients?.activated} active</div>
      {renderAwareness()}
      {
        surveys.map(survey => (
          <div key={"survey_" + survey.id}>
          {renderActivatedCount(survey)}
          </div>
        ))
      }
    </>
  );
}